import type { CreateInvestorPortalUserResponse, UserMfaStatus } from "../../../api/clientsManagementTypes";

export interface CreateInvestorPortalUserMessage {
  status: "success" | "warning";
  text: string;
  details: string[];
  copyText: string;
}

const noMfaSecurityNote =
  "Security note: This account uses No MFA. The user will NOT receive email verification codes at sign-in, which lowers the account's security.";

// Builds the result shown after creating an Investor Portal user. The operator copies this text
// and forwards it to whoever requested the account, so the copy block leads with the heading and
// drops the internal B2C user ID (kept on screen for the operator). The No-MFA security note is
// only added when No MFA was actually applied — when it failed, the account still falls back to
// the default MFA behavior, so warning the requester about missing codes would be wrong.
export const buildCreateInvestorPortalUserMessage = (
  resp: CreateInvestorPortalUserResponse,
  mfaStatus: UserMfaStatus
): CreateInvestorPortalUserMessage => {
  const baseText = resp.userCreated ? "User created" : "Existing B2C account linked";

  const nameLine = `Name: ${resp.displayName}`;
  const signInLine = `Sign-in email: ${resp.signInEmail}`;
  const passwordLine = `Temporary password: ${resp.temporaryPassword}`;

  // On screen the operator also sees the internal B2C User ID; the forwarded copy block omits it.
  const screenDetails = [nameLine, `User ID: ${resp.userId}`, signInLine, passwordLine];
  const copyCredentials = [nameLine, signInLine, passwordLine];

  // Heading and the block trailing the credentials depend on the MFA outcome. The No-MFA security
  // note only applies when No MFA was actually applied; on failure the account falls back to the
  // default MFA behavior, so we surface the backend warnings instead.
  const mfaFailed = !resp.mfaApplied;
  let text = baseText;
  let trailingBlock: string[] = [];
  if (mfaFailed) {
    text = `${baseText} — MFA not applied`;
    trailingBlock = resp.warnings;
  } else if (mfaStatus === "NoMFA") {
    text = `${baseText} — No MFA`;
    trailingBlock = [noMfaSecurityNote];
  }

  const details = [...screenDetails];
  const copyLines = [text, "", ...copyCredentials];
  if (trailingBlock.length > 0) {
    details.push("", ...trailingBlock);
    copyLines.push("", ...trailingBlock);
  }

  return { status: mfaFailed ? "warning" : "success", text, details, copyText: copyLines.join("\n") };
};
